/**
 * Enhanced Date Utilities for Consistent Date Handling
 * Fixes date discrepancy between admin portal and staff portal
 */

/**
 * Get current date in Singapore timezone (YYYY-MM-DD format)
 * Uses Intl.DateTimeFormat for accurate timezone conversion
 */
export const getSingaporeDate = (date = new Date()) => {
  // Use Intl.DateTimeFormat for accurate timezone conversion
  const singaporeDate = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Singapore',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
  
  return singaporeDate; // Returns YYYY-MM-DD format
};

/**
 * Get Singapore time components
 */
export const getSingaporeTime = (date = new Date()) => {
  const formatter = new Intl.DateTimeFormat('en-SG', {
    timeZone: 'Asia/Singapore',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  
  const parts = formatter.formatToParts(date);
  const partsObj = {};
  parts.forEach(part => {
    partsObj[part.type] = part.value;
  });
  
  return {
    date: `${partsObj.year}-${partsObj.month}-${partsObj.day}`,
    time: `${partsObj.hour}:${partsObj.minute}`,
    dateTime: new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Singapore' }))
  };
};

/**
 * Enhanced date normalization that handles edge cases better
 */
export const enhancedNormalizeDate = (dateInput) => {
  if (!dateInput) return null;
  
  let date;
  
  // Handle string inputs
  if (typeof dateInput === 'string') {
    // If already in YYYY-MM-DD format, validate and return
    if (dateInput.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const testDate = new Date(dateInput + 'T00:00:00.000Z');
      if (!isNaN(testDate.getTime())) {
        return dateInput;
      }
    }
    
    // Parse other string formats
    date = new Date(dateInput);
  } else if (dateInput instanceof Date) {
    date = new Date(dateInput);
  } else {
    return null;
  }
  
  if (isNaN(date.getTime())) return null;
  
  // Use Singapore timezone for normalization
  return getSingaporeDate(date);
};

/**
 * Standardized date display function for both portals
 * Priority: scheduledDate > jobDate > invoiceDate
 */
export const getStandardizedDisplayDate = (schedule) => {
  const displayDate = schedule.scheduledDate || schedule.jobDate || schedule.invoiceDate;
  
  if (!displayDate) return null;
  
  // Ensure consistent date format (YYYY-MM-DD)
  if (typeof displayDate === 'string' && displayDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return displayDate;
  }
  
  // If it's a different format, normalize it
  return enhancedNormalizeDate(displayDate);
};

/**
 * Get date range for Singapore timezone
 */
export const getSingaporeDateRange = (daysFromToday = 0) => {
  const today = new Date();
  const targetDate = new Date(today);
  targetDate.setDate(today.getDate() + daysFromToday);
  
  const singaporeDate = getSingaporeDate(targetDate);
  const singaporeTime = getSingaporeTime(targetDate);
  
  return {
    date: singaporeDate,
    dateTime: singaporeTime.dateTime,
    today: getSingaporeDate(today),
    tomorrow: getSingaporeDate(new Date(today.getTime() + 24 * 60 * 60 * 1000))
  };
};

/**
 * Format date for display with day name
 */
export const formatDateWithDay = (dateStr) => {
  if (!dateStr) return 'N/A';
  
  try {
    const date = new Date(dateStr + 'T00:00:00.000Z');
    return date.toLocaleDateString('en-SG', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  } catch (error) {
    return dateStr;
  }
};

/**
 * Format date for display (short format)
 */
export const formatDateShort = (dateStr) => {
  if (!dateStr) return 'N/A';
  
  try {
    const date = new Date(dateStr + 'T00:00:00.000Z');
    return date.toLocaleDateString('en-SG', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (error) {
    return dateStr;
  }
};

/**
 * Get day name from date string
 */
export const getDayName = (dateStr) => {
  if (!dateStr) return '';
  
  try {
    const date = new Date(dateStr + 'T00:00:00.000Z');
    return date.toLocaleDateString('en-SG', { weekday: 'long' });
  } catch (error) {
    return '';
  }
};

/**
 * Check if date is today in Singapore timezone
 */
export const isToday = (dateStr) => {
  if (!dateStr) return false;
  const today = getSingaporeDate();
  return dateStr === today;
};

/**
 * Check if date is tomorrow in Singapore timezone
 */
export const isTomorrow = (dateStr) => {
  if (!dateStr) return false;
  const tomorrow = getSingaporeDateRange(1).date;
  return dateStr === tomorrow;
};

/**
 * Check if date is in the past in Singapore timezone
 */
export const isPastDate = (dateStr) => {
  if (!dateStr) return false;
  const today = getSingaporeDate();
  return dateStr < today;
};

/**
 * Convert date string to Ant Design DatePicker format (dayjs object)
 */
export const convertDateToAntdFormat = (dateStr) => {
  if (!dateStr) return null;
  
  try {
    // Import dayjs dynamically to avoid circular dependencies
    const dayjs = require('dayjs');
    return dayjs(dateStr);
  } catch (error) {
    console.error('Error converting date to Ant Design format:', error);
    return null;
  }
};

/**
 * Convert Ant Design DatePicker value (dayjs object) to storage format (YYYY-MM-DD)
 */
export const convertAntdDateToStorage = (dayjsDate) => {
  if (!dayjsDate) return null;
  
  try {
    return dayjsDate.format('YYYY-MM-DD');
  } catch (error) {
    console.error('Error converting Ant Design date to storage format:', error);
    return null;
  }
};

/**
 * Convert date range to Ant Design RangePicker format
 */
export const convertDateRangeToAntdFormat = (startDate, endDate) => {
  if (!startDate || !endDate) return null;
  
  try {
    const dayjs = require('dayjs');
    return [dayjs(startDate), dayjs(endDate)];
  } catch (error) {
    console.error('Error converting date range to Ant Design format:', error);
    return null;
  }
};

/**
 * Convert Ant Design RangePicker value to storage format
 */
export const convertAntdRangeToStorage = (rangeValue) => {
  if (!rangeValue || !Array.isArray(rangeValue) || rangeValue.length !== 2) {
    return { startDate: null, endDate: null };
  }
  
  try {
    return {
      startDate: rangeValue[0] ? rangeValue[0].format('YYYY-MM-DD') : null,
      endDate: rangeValue[1] ? rangeValue[1].format('YYYY-MM-DD') : null
    };
  } catch (error) {
    console.error('Error converting Ant Design range to storage format:', error);
    return { startDate: null, endDate: null };
  }
};

/**
 * Legacy support - keep the original normalizeDate for backward compatibility
 * but use the enhanced version internally
 */
export const normalizeDate = enhancedNormalizeDate;

export default {
  getSingaporeDate,
  getSingaporeTime,
  enhancedNormalizeDate,
  getStandardizedDisplayDate,
  getSingaporeDateRange,
  formatDateWithDay,
  formatDateShort,
  getDayName,
  isToday,
  isTomorrow,
  isPastDate,
  normalizeDate,
  convertDateToAntdFormat,
  convertAntdDateToStorage,
  convertDateRangeToAntdFormat,
  convertAntdRangeToStorage
};
